/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the drug shipment table buttons component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import { Button } from '@material-ui/core';
import { Theme, makeStyles } from '@material-ui/core/styles';
import * as React from 'react';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import * as Dtos from '../../../api/Neon.Dtos';
import reportSettings from '../../../constants/reportSettings';
import { ReportExportFormat, createReportUrl } from '../../../utilities/createReportUrl';


/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IDrugShipmentTableButtonsProps {
    drugShipmentId: number;
    pharmacyId: number;
    shipmentStatus: Dtos.ShipmentStatus;
    setDrugShipmentId: React.Dispatch<React.SetStateAction<number | null>>;
    setDrugShipmentStatus: React.Dispatch<React.SetStateAction<Dtos.ShipmentStatus | null>>
    setDrugShipmentStatusModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setDrugShipmentReceiveModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setDrugShipmentDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    canAdministerDrugManagement: boolean | null;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(3)
    },
    buttonSet: {
        textAlign: 'right'
    },
    button: {
        marginLeft: theme.spacing(1),

        '&:first-child': {
            marginLeft: theme.spacing(0)
        }
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const DrugShipmentTableButtons: React.FunctionComponent<IDrugShipmentTableButtonsProps> = (props) => {
    const {
        drugShipmentId,
        shipmentStatus,
        setDrugShipmentId,
        setDrugShipmentStatus,
        setDrugShipmentReceiveModalOpen,
        canAdministerDrugManagement
    } = props;

    const classes = useStyles();

    const onReceivedClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        event.preventDefault();

        setDrugShipmentId(drugShipmentId);
        setDrugShipmentStatus(Dtos.ShipmentStatus.Available);
        setDrugShipmentReceiveModalOpen(true);
    }, [drugShipmentId, setDrugShipmentId, setDrugShipmentStatus, setDrugShipmentReceiveModalOpen]);

    if (shipmentStatus === Dtos.ShipmentStatus.New &&
        canAdministerDrugManagement) {
        return <New {...props} classes={classes} />
    }
    else if (shipmentStatus === Dtos.ShipmentStatus.Ordered &&
        canAdministerDrugManagement) {
        return <Ordered {...props} classes={classes}/>
    }
    else if (shipmentStatus === Dtos.ShipmentStatus.InTransit) {
        return <InTransit {...props} onReceivedClick={onReceivedClick} classes={classes} />;
    }
    else if (shipmentStatus === Dtos.ShipmentStatus.QuarantinedFromTransit) {
        return <QuarantinedFromTransit {...props} onReceivedClick={onReceivedClick} classes={classes} />;
    }

    return null;
};

interface ISubComponent extends IDrugShipmentTableButtonsProps {
    onReceivedClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    classes: ClassNameMap<string>;
}

const QuarantinedFromTransit: React.FC<ISubComponent> = ({onReceivedClick, classes}) => {
    return (
        <>
            <Button
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={onReceivedClick}
                size="small"
            >
                Received
            </Button>
        </>
    )
}

interface IInTransitProps extends IDrugShipmentTableButtonsProps {
    onReceivedClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    classes: ClassNameMap<string>;
}

const InTransit: React.FC<IInTransitProps> = (props) => {
    const {
        drugShipmentId,
        setDrugShipmentId,
        setDrugShipmentStatus,
        setDrugShipmentStatusModalOpen,
        canAdministerDrugManagement,
        onReceivedClick,
        classes
    } = props;

    const onLostClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        event.preventDefault();

        setDrugShipmentId(drugShipmentId);
        setDrugShipmentStatus(Dtos.ShipmentStatus.Lost);
        setDrugShipmentStatusModalOpen(true);
    }, [drugShipmentId, setDrugShipmentId, setDrugShipmentStatus, setDrugShipmentStatusModalOpen]);

    const onQuarantinedClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        event.preventDefault();

        setDrugShipmentId(drugShipmentId);
        setDrugShipmentStatus(Dtos.ShipmentStatus.QuarantinedFromTransit);
        setDrugShipmentStatusModalOpen(true);
    }, [drugShipmentId, setDrugShipmentId, setDrugShipmentStatus, setDrugShipmentStatusModalOpen]);

    return <>
        <Button
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={onReceivedClick}
            size="small"
        >
            Received
        </Button>
        <Button
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={onQuarantinedClick}
            size="small"
        >
            Quarantined
        </Button>
        {
            canAdministerDrugManagement &&
            <Button
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={onLostClick}
                size="small"
            >
                Lost
            </Button>
        }
    </>;
}

interface IOrderedProps extends IDrugShipmentTableButtonsProps {
    classes: ClassNameMap<string>;
}

const Ordered: React.FC<IOrderedProps> = (props) => {
    const {
        drugShipmentId,
        setDrugShipmentId,
        setDrugShipmentStatus,
        setDrugShipmentStatusModalOpen,
        classes
    } = props;

    const onShipClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        event.preventDefault();

        setDrugShipmentId(drugShipmentId);
        setDrugShipmentStatus(Dtos.ShipmentStatus.InTransit);
        setDrugShipmentStatusModalOpen(true);
    }, [drugShipmentId, setDrugShipmentId, setDrugShipmentStatus, setDrugShipmentStatusModalOpen]);

    return <>
        <Button
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={onShipClick}
            size="small"
        >
            Shipped
        </Button>
    </>;
}

interface INewProps extends IDrugShipmentTableButtonsProps {
    classes: ClassNameMap<string>;
}

const New: React.FC<INewProps> = (props) => {
    const {
        drugShipmentId,
        setDrugShipmentId,
        setDrugShipmentStatus,
        setDrugShipmentStatusModalOpen,
        setDrugShipmentDeleteModalOpen,
        classes
    } = props;

    const onOrderFormClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        event.preventDefault();

        window.open(createReportUrl(reportSettings.drugShipmentForm,
            [
                { paramName: 'shipmentid', paramValue: drugShipmentId.toString() }
            ],
            ReportExportFormat.Pdf));
    }, [drugShipmentId]);

    const onOrderClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        event.preventDefault();

        setDrugShipmentId(drugShipmentId);
        setDrugShipmentStatus(Dtos.ShipmentStatus.Ordered);
        setDrugShipmentStatusModalOpen(true);
    }, [drugShipmentId, setDrugShipmentId, setDrugShipmentStatus, setDrugShipmentStatusModalOpen]);

    const onDeleteClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        event.preventDefault();

        setDrugShipmentId(drugShipmentId);
        setDrugShipmentDeleteModalOpen(true);
    }, [drugShipmentId, setDrugShipmentId, setDrugShipmentDeleteModalOpen]);

    return <>
        <Button
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={onOrderFormClick}
            size="small"
        >
            Order Form
        </Button>
        <Button
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={onOrderClick}
            size="small"
        >
            Order
        </Button>
        <Button
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={onDeleteClick}
            size="small"
        >
            Delete
        </Button>
    </>;
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default DrugShipmentTableButtons;